import { Box, Flex } from '@chakra-ui/react'
import classnames from 'classnames'
import React, { createRef, Ref, useState } from 'react'
import { Trans, t } from '@lingui/macro'

import { SurveyStatusEnum } from '../../../../../shared/enums/surveys'
import { linkBuilder } from '../../../../../shared/linkBuilder'
import { navigateTo } from '../../../app/util/appHistory'
import { ButtonPrimary, ButtonLight } from 'front-end/src/components'

import { Checkbox } from '../../../components/common/form/checkbox/Checkbox'
import Field from '../../../components/common/form/field/Field'
import ResettableInput from '../../../components/common/form/input/ResettableInput'
import Loader from '../../../components/common/loader/Loader'
import { Modal } from '../../../components/common/modal/Modal'
import { ISurvey } from '../../surveys/types'
import { useSearchSurveysQuery } from '../operations/searchSurveys.codegen'

interface IProps {
  onCancel: () => void
  onHide: () => void
  campaignId: number
}

const CampaignSurveyShareActionModal = ({
  campaignId,
  onHide,
  onCancel
}: IProps) => {
  const [query, setQuery] = useState<string>('')
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [searchedSurveys, setSearchedSurveys] = useState<[]>([])
  const inputRef: Ref<HTMLInputElement> = createRef<HTMLInputElement>()
  const { data, loading } = useSearchSurveysQuery({
    variables: {
      status: SurveyStatusEnum.ACTIVE
    }
  })

  if (loading) {
    return <Loader />
  }

  const surveys = data.admin.surveys

  const handleSearch = (searchVal): void => {
    setQuery(searchVal)
    const foundSurveys = searchVal
      ? surveys.filter((survey) => {
          return survey.title.toLowerCase().includes(searchVal.toLowerCase())
        })
      : surveys
    setSearchedSurveys(foundSurveys)
  }

  const handleSubmit = (): void => {
    navigateTo(
      linkBuilder.campaignNewSurveyShareAction(campaignId, selectedIds)
    )
  }

  const handleSelectSurvey = (surveyId: number): void => {
    const temporalSelectedIds: number[] = selectedIds
    if (!isSurveySelected(surveyId)) {
      temporalSelectedIds.push(surveyId)
    } else {
      temporalSelectedIds.splice(selectedIds.indexOf(surveyId), 1)
    }
    setSelectedIds([...temporalSelectedIds])
  }

  const isSurveySelected = (surveyId: number): boolean => {
    return selectedIds.indexOf(surveyId) !== -1
  }

  const surveyResult = searchedSurveys.length > 0 ? searchedSurveys : surveys

  return (
    <Modal
      onHide={onHide}
      header={t`Surveys share`}
      subHeader={t`Choose surveys to add to this campaign`}
      className="CampaignSurveyShareActionModal"
      show
      onShown={() => {
        setSelectedIds([])
        inputRef.current?.focus()
      }}
      onHidden={() => {
        setQuery('')
        setSelectedIds([])
      }}
      controls={
        <>
          <ButtonLight onClick={onCancel}>
            <Trans>Cancel</Trans>
          </ButtonLight>
          <ButtonPrimary
            onClick={() => handleSubmit()}
            disabled={selectedIds.length < 1}
          >
            <Trans>Add</Trans>
          </ButtonPrimary>
        </>
      }
    >
      <Field stretch>
        <ResettableInput
          title={t`Survey name`}
          placeholder={t`Survey name...`}
          value={query}
          inputRef={inputRef}
          onChange={(searchVal) => {
            handleSearch(searchVal)
          }}
          showResetButton={!!query}
          showSearchIcon={true}
          onReset={() => {
            setQuery('')
          }}
        />
      </Field>

      <div className="List">
        {loading && <Loader />}
        {!loading && surveyResult.length === 0 && (
          <p className="List__Row__noResults">
            <Trans>No results</Trans>
          </p>
        )}
        {!loading &&
          surveyResult.map((survey: ISurvey) => (
            <label
              key={survey.id}
              className={classnames('List__Row', {
                active: isSurveySelected(survey.id)
              })}
            >
              <Flex alignItems={'center'}>
                <Checkbox
                  isChecked={isSurveySelected(survey.id)}
                  onChange={() => {
                    handleSelectSurvey(survey.id)
                  }}
                />
                <Box ml={'4px'} className="List__Row__surveyName">
                  {survey ? (
                    survey.title
                  ) : (
                    <i>
                      <Trans>N/A</Trans>
                    </i>
                  )}
                </Box>
              </Flex>
            </label>
          ))}
      </div>
    </Modal>
  )
}

export default CampaignSurveyShareActionModal
